import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { OPTIMISTIC_REPLY_PREFIX } from './createOptimisticReply';

const SCROLL_BOTTOM_THRESHOLD_PX = 16;
const PAGINATION_SEAM_OFFSET_PX = 80;

const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

type Params = {
  scrollRef: RefObject<HTMLDivElement>;
  isInitialLoading: boolean;
  lastReplyId: string | undefined;
  repliesCount: number;
};

export const useReplyScrollBehavior = ({
  scrollRef,
  isInitialLoading,
  lastReplyId,
  repliesCount,
}: Params) => {
  const [hasNewComment, setHasNewComment] = useState(false);
  const previousLastReplyId = usePrevious(lastReplyId);
  const previousRepliesCount = usePrevious(repliesCount) ?? 0;
  const pendingOwnScrollRef = useRef(false);
  const pendingOwnThreadRef = useRef(false);
  const hasInitialScrolledRef = useRef(false);
  const preFetchScrollRef = useRef<{ height: number; top: number } | null>(
    null,
  );

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior) => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior,
      });
    },
    [scrollRef],
  );

  useEffect(() => {
    if (isInitialLoading) return;

    if (!hasInitialScrolledRef.current) {
      hasInitialScrolledRef.current = true;
      scrollToBottom('instant');
      return;
    }

    const bottomChanged = lastReplyId !== previousLastReplyId;
    const ownThreadInsert =
      pendingOwnThreadRef.current && repliesCount > previousRepliesCount;
    if (ownThreadInsert) pendingOwnThreadRef.current = false;

    if (bottomChanged) {
      const isOptimisticReplacement =
        previousLastReplyId?.startsWith(OPTIMISTIC_REPLY_PREFIX) === true &&
        lastReplyId !== undefined &&
        !lastReplyId.startsWith(OPTIMISTIC_REPLY_PREFIX);
      if (isOptimisticReplacement) return;

      if (ownThreadInsert) return;

      if (pendingOwnScrollRef.current) {
        pendingOwnScrollRef.current = false;
        scrollToBottom('smooth');
        return;
      }

      setHasNewComment(true);
      return;
    }

    const olderPagePrepended =
      repliesCount > previousRepliesCount &&
      preFetchScrollRef.current !== null &&
      scrollRef.current !== null;
    if (olderPagePrepended && preFetchScrollRef.current && scrollRef.current) {
      const heightDelta =
        scrollRef.current.scrollHeight - preFetchScrollRef.current.height;
      const preservedTop = preFetchScrollRef.current.top + heightDelta;
      scrollRef.current.scrollTo({
        top: preservedTop - PAGINATION_SEAM_OFFSET_PX,
        behavior: 'instant',
      });
      preFetchScrollRef.current = null;
    }
    // Deps are deliberately limited to the current-render inputs. The effect
    // compares them against the PREVIOUS render's values (previousLastReplyId /
    // previousRepliesCount, captured by usePrevious) and against imperative
    // refs; adding those to the array would re-run on their post-render update
    // and break the previous-vs-current comparison.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialLoading, lastReplyId, repliesCount, scrollToBottom]);

  const markOwnSubmit = useCallback(() => {
    pendingOwnScrollRef.current = true;
  }, []);

  const markOwnThreadSubmit = useCallback(() => {
    pendingOwnThreadRef.current = true;
  }, []);

  const captureScrollBeforePagination = useCallback(() => {
    if (scrollRef.current) {
      preFetchScrollRef.current = {
        height: scrollRef.current.scrollHeight,
        top: scrollRef.current.scrollTop,
      };
    }
  }, [scrollRef]);

  const scrollToNewComment = useCallback(() => {
    setHasNewComment(false);
    scrollToBottom('smooth');
  }, [scrollToBottom]);

  const handleScroll = useCallback(() => {
    if (!hasNewComment || !scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollHeight - scrollTop - clientHeight <= SCROLL_BOTTOM_THRESHOLD_PX) {
      setHasNewComment(false);
    }
  }, [hasNewComment, scrollRef]);

  return {
    hasNewComment,
    markOwnSubmit,
    markOwnThreadSubmit,
    captureScrollBeforePagination,
    scrollToNewComment,
    handleScroll,
  };
};
